import { useState } from "react";
import { useForm } from "react-hook-form";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import { Icon } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { CursorTable, type CursorTableColumn } from "@core/table";
import {
  DialogHeader,
  EmptyState,
  Field,
  FeedbackState,
  DialogActions,
  EditorModal,
  type EditorModalTarget,
  PortalButton,
  RowIconAction,
  inputStyle,
  useToast,
} from "@portal/common";
import {
  catalogLabel,
  resolveManualRule,
  type OriginFilter,
  type PointMovement,
  type RegisterPointsInput,
} from "./api";
import {
  registerPointsSchema,
  voidMovementSchema,
  type RegisterPointsValues,
  type VoidMovementValues,
} from "./pointSchemas";
import {
  useAdjustmentReasons,
  useManualMemberRules,
  useMovements,
  usePointMutations,
} from "./pointQueries";

// admin.jsx:1226: la fila anulada se atenúa, nunca se tacha (G-006). Un solo
// valor, no repetido literal en cada una de las siete celdas — cada columna
// envuelve su contenido con `rowOpacity(mv)`.
const VOIDED_OPACITY = 0.55;
const rowOpacity = (mv: PointMovement) =>
  mv.status === "voided" ? VOIDED_OPACITY : 1;

const shortDate = (value: string) => {
  // `occurredAt` es date-time; se muestra solo la fecha, formato admin.jsx
  // (día · abreviatura de mes de tres letras · año), igual criterio que
  // vehicles/dateFormat.ts pero sin duplicar su tabla de meses acá — se
  // resuelve con Intl y se corrige a mano el único mes (setiembre) donde el
  // motor ICU de este entorno da una abreviatura de cuatro letras.
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = date
    .toLocaleDateString("es-EC", { month: "short" })
    .replace(/\.$/, "")
    .slice(0, 3);
  return `${day} ${month} ${date.getFullYear()}`;
};

const originChip = (mv: PointMovement) => {
  // G-002: el libro mayor produce cuatro clases de origen. El prototipo solo
  // dibuja dos (su seed nunca genera canje ni ajuste) — canje y ajuste
  // reusan el mismo chip neutro que acción propia, rotulado con su nombre;
  // es presentación de columna, no un valor del filtro (G-001).
  switch (mv.origin) {
    case "referral":
      return (
        <span
          className="stat stat-gold"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 150,
            display: "inline-block",
          }}
          title={`${mv.referral?.name ?? ""} · ${mv.action ?? ""}`}
        >
          Referido · {mv.referral?.name ?? "—"}
        </span>
      );
    case "redeem":
      return <span className="stat stat-neutral">Canje</span>;
    case "adjustment":
      return <span className="stat stat-neutral">Ajuste</span>;
    default:
      return <span className="stat stat-neutral">Acción propia</span>;
  }
};

// PLAN-009 §3: SPEC-012 criterio 12 — ninguna condición depende del texto
// del aviso. `OLD_DATE` es una reconfirmación (aviso, se queda en el
// formulario) que ahora se señaliza con un booleano explícito
// (`needsDateConfirmation`), no leyendo si el texto incluye "¿Continuar?".
// El resto de los códigos son errores de servicio: van al toast.
const REGISTER_ERROR_MESSAGES: Partial<Record<string, string>> = {
  NO_ACTIVE_RULE:
    "No hay una regla activa para esta acción en la fecha indicada",
  MEMBER_INACTIVE: "El miembro está inactivo y no puede acumular",
};
const OLD_DATE_NOTICE = "La fecha indicada tiene más de 30 días. ¿Continuar?";
function reportRegisterError(
  toast: ReturnType<typeof useToast>,
  error: unknown,
  onOldDate: () => void,
) {
  if (error instanceof ApiError && error.code === "OLD_DATE") {
    onOldDate();
    markErrorHandled(error);
    return;
  }
  // RF-MIE-14/G-008: el mensaje conserva "Usos: N de N" — el backend ya lo
  // arma en `detail` (20-members-points.yaml, ejemplo CAP_REACHED); no hay
  // campo estructurado propio para reconstruirlo del lado del cliente. La
  // rama sigue resuelta por `code`, solo el contenido final viene del
  // servidor para este código puntual.
  if (error instanceof ApiError && error.code === "CAP_REACHED") {
    toast.show(
      error.problem?.detail ??
        "Esta acción ya alcanzó su tope por período. Usos: —",
      "error",
    );
    markErrorHandled(error);
    return;
  }
  reportError(toast, error, REGISTER_ERROR_MESSAGES);
}

const VOID_ERROR_MESSAGES: Partial<Record<string, string>> = {
  MOVEMENT_VOIDED: "Este movimiento ya fue anulado",
  PRIZE_DELIVERED:
    "Este movimiento sustenta un premio ya entregado y no puede anularse",
  // QA-MTZ-002 #8: un canje se revierte desde Consumos, nunca se anula acá.
  REDEMPTION_MOVEMENT:
    "Este movimiento corresponde a un canje; revierte el consumo desde la pestaña Consumos.",
};

function RegisterForm({
  memberName,
  onSubmit,
  onCancel,
  submitting,
  needsDateConfirmation,
  modal,
}: {
  memberName?: string | undefined;
  onSubmit: (value: RegisterPointsInput, confirmed: boolean) => void;
  onCancel: () => void;
  submitting?: boolean;
  needsDateConfirmation: boolean;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el formulario ES el diálogo.
  // El título y los botones dejan de desplazarse con el contenido.
  modal: EditorModalTarget;
}) {
  const rules = useManualMemberRules();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<RegisterPointsValues>({
    defaultValues: {
      actionId: "",
      date: "",
      reference: "",
      evidence: "",
    },
  });
  const actionId = watch("actionId");
  const date = watch("date");
  const manualRules = rules.data ?? [];
  const resolution =
    actionId && date
      ? resolveManualRule(manualRules, actionId, date)
      : undefined;
  // FAIL 3 (ola 1): `previewPoints` solo lleva un valor cuando la regla
  // resuelta cubre exactamente la fecha indicada (`kind: "exact"`). Un
  // `undefined` no es "cero puntos" — es "no hay una cifra que el cliente
  // pueda sostener"; el resumen y la caja de vista previa lo rotulan en
  // palabras en vez de mostrar el número de otra regla (la vigente hoy, que
  // puede no ser la aplicable a una fecha pasada).
  const previewPoints =
    resolution?.kind === "exact" ? resolution.rule.points : undefined;
  const previewSelected = Boolean(actionId && date);
  const reference = watch("reference");
  const actionName =
    manualRules.find((entry) => entry.action.id === actionId)?.action.name ??
    "";

  return (
    <EditorModal
      modal={modal}
      // Punto #21 (revisión en vivo 2026-09-22): encabezado común de
      // diálogos; el eyebrow es el panel desde el que se abre.
      header={
        <DialogHeader
          eyebrow="Estado de cuenta de puntos"
          title="Registrar puntos"
        />
      }
      footer={
        <DialogActions onCancel={onCancel}>
          <PortalButton type="submit" size="small" disabled={submitting}>
            {submitting
              ? "Guardando…"
              : needsDateConfirmation
                ? "Confirmar de todas formas"
                : "Confirmar registro"}
          </PortalButton>
        </DialogActions>
      }
      onSubmit={handleSubmit((value) => {
        const parsed = registerPointsSchema.safeParse(value);
        if (!parsed.success) {
          for (const issue of parsed.error.issues)
            setError(issue.path[0] as keyof RegisterPointsValues, {
              message: issue.message,
            });
          return;
        }
        const data = parsed.data;
        // El aviso `OLD_DATE` ya llegó del servidor en este render (prop
        // `needsDateConfirmation`, seteada por el `onError` de la corrida
        // anterior): un segundo clic sobre el mismo formulario reenvía con
        // `confirmed: true`. SPEC-012 criterio 12: la señal es un booleano
        // explícito, nunca una condición que lee el texto del aviso.
        const confirmed = needsDateConfirmation;
        onSubmit(
          {
            actionId: data.actionId,
            date: data.date,
            reference: data.reference,
            ...(data.evidence?.trim()
              ? { evidence: data.evidence.trim() }
              : {}),
          },
          confirmed,
        );
      })}
    >
      {memberName ? (
        <div
          style={{
            font: `400 12px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
            marginBottom: 16,
          }}
        >
          {memberName}
        </div>
      ) : null}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.4fr",
          gap: "0 16px",
        }}
      >
        <Field label="Fecha" required error={errors.date?.message}>
          <input
            aria-label="Fecha"
            type="date"
            style={inputStyle}
            {...register("date")}
          />
        </Field>
        <Field
          label="Acción realizada por el miembro"
          required
          error={errors.actionId?.message}
        >
          <select
            aria-label="Acción realizada por el miembro"
            style={inputStyle}
            {...register("actionId")}
          >
            <option value="">Selecciona una acción</option>
            {manualRules.map((entry) => (
              <option key={entry.action.id} value={entry.action.id}>
                {entry.action.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="label" style={{ margin: "16px 0 8px" }}>
        Puntos a otorgar
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 16px",
          borderRadius: 13,
          border: `1px solid ${tokens.gold.line}`,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            font: `600 28px ${tokens.font.display}`,
            color: tokens.gold.light,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {previewPoints ?? "—"}
        </span>
        <div
          style={{
            flex: 1,
            font: `400 11.5px/1.5 ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {previewPoints !== undefined
            ? "Según regla vigente del motor de puntos · no editable."
            : previewSelected
              ? "No hay una regla que cubra exactamente esta fecha · el motor de puntos determinará el valor al guardar."
              : "Selecciona una acción y una fecha para ver la vista previa."}
        </div>
      </div>

      <Field label="Referencia" required error={errors.reference?.message}>
        <input
          aria-label="Referencia"
          style={inputStyle}
          placeholder="Ej. compra confirmada de un referido, SAP-000000"
          {...register("reference")}
        />
      </Field>
      <div
        style={{
          font: `400 11px ${tokens.font.ui}`,
          color: tokens.text.disabled,
          margin: "-4px 0 12px",
        }}
      >
        Detalle que identifica el hecho que origina el movimiento.
      </div>

      {/* G-003: evidencia es texto libre — no se adjuntan archivos
          (numerales 3.1 y 9). El <input type="file"> del prototipo
          (admin.jsx:1286-1291) no se implementa. */}
      <Field label="Evidencia">
        <input
          aria-label="Evidencia"
          style={inputStyle}
          {...register("evidence")}
        />
      </Field>
      <div
        style={{
          font: `400 11px ${tokens.font.ui}`,
          color: tokens.text.disabled,
          margin: "-4px 0 12px",
        }}
      >
        Opcional. Texto libre: no se adjuntan archivos.
      </div>

      <div
        style={{
          padding: "14px 16px",
          borderRadius: 13,
          border: `1px solid ${tokens.surface.line2}`,
          background: tokens.surface.surface,
          marginBottom: 14,
        }}
      >
        <div className="label" style={{ marginBottom: 8 }}>
          Resumen antes de guardar
        </div>
        <div
          style={{
            font: `400 12.5px/1.7 ${tokens.font.ui}`,
            color: tokens.text.secondary,
          }}
        >
          {previewPoints !== undefined ? (
            <>
              Se otorgarán{" "}
              <b style={{ color: tokens.gold.light }}>{previewPoints} puntos</b>
            </>
          ) : (
            <>
              El motor de puntos determinará los puntos a otorgar al guardar —
              no hay una cifra confirmada para esta fecha
            </>
          )}
          {memberName ? ` a ${memberName}` : ""}
          {actionName ? ` por «${actionName}»` : ""}
          {date ? `, con fecha ${date}` : ""}
          {reference?.trim() ? ` y referencia «${reference.trim()}»` : ""}.
        </div>
      </div>

      {needsDateConfirmation ? (
        <div
          role="alert"
          style={{
            marginTop: 6,
            marginBottom: 14,
            padding: "11px 14px",
            borderRadius: 11,
            border: `1px solid ${tokens.danger.line}`,
            background: tokens.danger.dim,
            font: `400 12px ${tokens.font.ui}`,
            color: tokens.danger.base,
          }}
        >
          {OLD_DATE_NOTICE}
        </div>
      ) : null}
    </EditorModal>
  );
}

function VoidForm({
  movement,
  onSubmit,
  onCancel,
  submitting,
  modal,
}: {
  movement: PointMovement;
  onSubmit: (value: VoidMovementValues) => void;
  onCancel: () => void;
  submitting?: boolean;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el formulario ES el diálogo.
  modal: EditorModalTarget;
}) {
  const reasons = useAdjustmentReasons();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<VoidMovementValues>({
    defaultValues: { adjustmentReasonId: "", detail: "" },
  });
  return (
    <EditorModal
      modal={modal}
      // Punto #21 (revisión en vivo 2026-09-22): encabezado común.
      header={
        <DialogHeader
          eyebrow="Estado de cuenta de puntos"
          title="Anular movimiento"
        />
      }
      footer={
        <DialogActions onCancel={onCancel}>
          {/* `tone="danger"` reemplaza el `.btn btn-sm` con los tres tokens
            de peligro repetidos a mano; el resultado visual es el mismo
            (`.btn-danger`, base.css) pero sale del primitivo. */}
          <PortalButton
            type="submit"
            tone="danger"
            size="small"
            disabled={submitting}
          >
            {submitting ? "Anulando…" : "Anular movimiento"}
          </PortalButton>
        </DialogActions>
      }
      onSubmit={handleSubmit((value) => {
        const parsed = voidMovementSchema.safeParse(value);
        if (!parsed.success) {
          for (const issue of parsed.error.issues)
            setError(issue.path[0] as keyof VoidMovementValues, {
              message: issue.message,
            });
          return;
        }
        onSubmit(parsed.data);
      })}
    >
      <p
        style={{
          font: `400 12px ${tokens.font.ui}`,
          color: tokens.text.tertiary,
        }}
      >
        {movement.reference ?? "—"} · {movement.points > 0 ? "+" : ""}
        {movement.points} puntos · {shortDate(movement.occurredAt)}
      </p>
      <Field
        label="Motivo del ajuste"
        required
        error={errors.adjustmentReasonId?.message}
      >
        <select
          aria-label="Motivo del ajuste"
          style={inputStyle}
          {...register("adjustmentReasonId")}
        >
          <option value="">Selecciona un motivo…</option>
          {(reasons.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {catalogLabel(entry)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Detalle adicional" required error={errors.detail?.message}>
        <textarea
          aria-label="Detalle adicional"
          style={{ ...inputStyle, minHeight: 66, resize: "vertical" }}
          {...register("detail")}
        />
      </Field>
      <div
        style={{
          font: `400 11.5px/1.6 ${tokens.font.ui}`,
          color: tokens.text.disabled,
        }}
      >
        Se generará un movimiento contrario de {movement.points > 0 ? "−" : "+"}
        {Math.abs(movement.points)} puntos, visible en la tabla.
      </div>
    </EditorModal>
  );
}

export function PointsPanel({
  memberId,
  memberName,
}: {
  memberId: string;
  memberName?: string | undefined;
}) {
  const [origin, setOrigin] = useState<OriginFilter>("all");
  const movements = useMovements(memberId, origin);
  const mutations = usePointMutations(memberId);
  const toast = useToast();
  const [registering, setRegistering] = useState(false);
  const [needsDateConfirmation, setNeedsDateConfirmation] = useState(false);
  const [voiding, setVoiding] = useState<PointMovement | null>(null);
  // FAIL 1/2 (ola 1): la clave de idempotencia identifica un intento lógico,
  // no una invocación. Se genera una sola vez al empezar la operación —abrir
  // el formulario de registro, elegir un movimiento para anular— y se reusa
  // en todo reintento de ese mismo intento (la reconfirmación de OLD_DATE
  // incluida). Solo se renueva al iniciar una operación nueva: abrir el
  // formulario de nuevo, o elegir otro movimiento distinto para anular.
  const [registerKey, setRegisterKey] = useState<string | null>(null);
  const [voidKey, setVoidKey] = useState<string | null>(null);

  const rows = movements.data?.pages.flatMap((page) => page.items) ?? [];
  const lastPage = movements.data?.pages.at(-1);
  // ADR-008: el saldo se deriva de la suma de los movimientos vigentes y lo
  // devuelve el servidor en cada página — nunca se recalcula ni se cachea
  // acá aparte. EXTRA-A: `historicalAccumulated` viaja en la misma respuesta
  // y deliberadamente no se lee ni se muestra: ese requisito es RF-BEN-14,
  // propio de la superficie app (SPEC-104), no de RF-MIE-11 del portal.
  const balance = movements.data?.pages[0]?.balance ?? 0;

  const submitRegister = (input: RegisterPointsInput, confirmed: boolean) => {
    // `registerKey` ya quedó fijado al abrir el formulario (ver el `onClick`
    // de "Registrar puntos" más abajo); el fallback solo cubre una apertura
    // que por algún motivo no pasó por ese punto.
    const idempotencyKey = registerKey ?? crypto.randomUUID();
    mutations.register.mutate(
      {
        input: { ...input, confirmed },
        idempotencyKey,
      },
      {
        onSuccess: () => {
          setRegistering(false);
          setNeedsDateConfirmation(false);
          setRegisterKey(null);
          toast.show("Puntos registrados.");
        },
        onError: (e) =>
          reportRegisterError(toast, e, () => setNeedsDateConfirmation(true)),
      },
    );
  };

  const submitVoid = (value: VoidMovementValues) => {
    if (!voiding) return;
    // Mismo criterio que `submitRegister`: `voidKey` se fija al elegir el
    // movimiento a anular y se reusa en cualquier reintento de esa misma
    // anulación.
    const idempotencyKey = voidKey ?? crypto.randomUUID();
    mutations.void.mutate(
      {
        movementId: voiding.id,
        input: value,
        idempotencyKey,
      },
      {
        onSuccess: () => {
          setVoiding(null);
          setVoidKey(null);
          toast.show("Movimiento anulado");
        },
        onError: (e) => reportError(toast, e, VOID_ERROR_MESSAGES),
      },
    );
  };

  const columns: CursorTableColumn<PointMovement>[] = [
    {
      key: "date",
      header: "Fecha",
      width: "86px",
      render: (mv) => (
        <span
          style={{
            opacity: rowOpacity(mv),
            font: `400 11.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {shortDate(mv.occurredAt)}
        </span>
      ),
    },
    {
      key: "action",
      header: "Acción",
      width: "1.3fr",
      render: (mv) => (
        <span
          style={{
            opacity: rowOpacity(mv),
            font: `500 12px ${tokens.font.ui}`,
            color: tokens.text.secondary,
          }}
        >
          {mv.action ?? "—"}
        </span>
      ),
    },
    {
      key: "reference",
      header: "Referencia",
      width: "1.5fr",
      render: (mv) => (
        <span
          style={{
            opacity: rowOpacity(mv),
            font: `400 11.5px/1.4 ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {mv.reference ?? "—"}
        </span>
      ),
    },
    {
      key: "origin",
      header: "Origen",
      width: "160px",
      render: (mv) => (
        <span style={{ opacity: rowOpacity(mv) }}>{originChip(mv)}</span>
      ),
    },
    {
      key: "points",
      header: "Puntos",
      width: "88px",
      render: (mv) => (
        <span
          style={{
            opacity: rowOpacity(mv),
            font: `600 13px ${tokens.font.ui}`,
            fontVariantNumeric: "tabular-nums",
            color: mv.points >= 0 ? tokens.gold.light : tokens.danger.base,
          }}
        >
          {mv.points > 0 ? "+" : ""}
          {mv.points}
        </span>
      ),
    },
    {
      key: "registeredBy",
      header: "Registrado por",
      width: "132px",
      render: (mv) => (
        <span
          style={{
            opacity: rowOpacity(mv),
            font: `400 11.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {mv.registeredBy ?? "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Estado",
      width: "100px",
      // El `Tooltip` de "Anular" está posicionado en absoluto: con el
      // `overflow: hidden` por defecto de la celda se recortaría contra su
      // borde. La columna es de ancho fijo con un badge `nowrap` y un botón
      // de ícono, así que soltar el recorte no desborda nada.
      overflow: "visible",
      render: (mv) => (
        <div
          style={{
            opacity: rowOpacity(mv),
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "flex-start",
          }}
        >
          <span
            className={`stat stat-${mv.status === "current" ? "silver" : "neutral"}`}
          >
            {mv.status === "current" ? "Vigente" : "Anulado"}
          </span>
          {/* G-004: el origen ya no oculta "Anular" — visible mientras el
              movimiento esté vigente y sus puntos no sean cero.
              Revisión en vivo 2026-09-22, punto #10 (barrido): estaba
              dibujado con `.btn-link` a 11px, apilado justo debajo del badge
              de estado "Vigente" — o sea, texto plano gris pegado a una
              etiqueta informativa, exactamente la confusión estado/acción del
              punto #11. Anular genera un movimiento contrario de puntos: es
              tan consecuente como "Revertir" en Consumos, así que lleva el
              mismo ícono + tooltip y el mismo tono de peligro.
              QA-MTZ-002 #8: la excepción es el canje (`origin` "redeem"):
              anularlo dejaba el consumo sin su movimiento y revertirlo
              después fallaba. Se revierte desde Consumos; la API también lo
              rechaza (`REDEMPTION_MOVEMENT`). */}
          {mv.status === "current" &&
          mv.points !== 0 &&
          mv.origin !== "redeem" ? (
            <Can module="members" action="canEdit">
              <RowIconAction
                label="Anular"
                icon="revert"
                tone="danger"
                onClick={() => {
                  setVoidKey(crypto.randomUUID());
                  setVoiding(mv);
                }}
              />
            </Can>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="points-panel-content">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 0 14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span
            style={{
              font: `600 32px ${tokens.font.display}`,
              color: tokens.gold.light,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {balance.toLocaleString("es-EC")}
          </span>
          <span
            style={{
              font: `600 10.5px ${tokens.font.ui}`,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: tokens.text.tertiary,
            }}
          >
            puntos vigentes
          </span>
        </div>
        <Can module="members" action="canCreate">
          <PortalButton
            size="small"
            onClick={() => {
              setNeedsDateConfirmation(false);
              setRegisterKey(crypto.randomUUID());
              setRegistering(true);
            }}
          >
            <Icon name="plus" size={15} />
            Registrar puntos
          </PortalButton>
        </Can>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <select
          aria-label="Origen"
          value={origin}
          onChange={(e) => setOrigin(e.target.value as OriginFilter)}
          style={{ ...inputStyle, width: "auto", minWidth: 160 }}
        >
          <option value="all">Origen · todos</option>
          <option value="own">Origen · Propia</option>
          <option value="referral">Origen · Referido</option>
        </select>
      </div>

      {movements.isLoading ? (
        <FeedbackState kind="loading" />
      ) : movements.isError ? (
        <EmptyState kind="error" />
      ) : rows.length ? (
        <CursorTable
          columns={columns}
          rows={rows}
          rowKey={(mv) => mv.id}
          page={{
            nextCursor: lastPage?.meta.next ?? null,
            totalCount: lastPage?.meta.total ?? rows.length,
          }}
          onLoadMore={() => movements.fetchNextPage()}
          isLoadingMore={movements.isFetchingNextPage}
          minWidth={1120}
          showFooter={Boolean(lastPage?.meta.next)}
        />
      ) : (
        <div
          style={{
            padding: "30px 20px 34px",
            textAlign: "center",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
            borderTop: `1px solid ${tokens.surface.line}`,
          }}
        >
          Este miembro aún no tiene movimientos de puntos.
        </div>
      )}

      {registering ? (
        <RegisterForm
          memberName={memberName}
          onSubmit={submitRegister}
          onCancel={() => setRegistering(false)}
          submitting={mutations.register.isPending}
          needsDateConfirmation={needsDateConfirmation}
          modal={{
            label: "Registrar puntos",
            width: 640,
            onClose: () => setRegistering(false),
          }}
        />
      ) : null}

      {voiding ? (
        <VoidForm
          movement={voiding}
          onSubmit={submitVoid}
          onCancel={() => setVoiding(null)}
          submitting={mutations.void.isPending}
          modal={{
            label: "Anular movimiento",
            width: 480,
            role: "alertdialog",
            onClose: () => setVoiding(null),
          }}
        />
      ) : null}
    </div>
  );
}
